import {
  Add,
  AddOutlined,
  CloudSyncOutlined,
  Download,
  MoreVertOutlined,
  PostAddOutlined,
} from '@mui/icons-material';
import {
  Box,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { clearExternalInvitations } from '@/domain/external-invitation/actions/clear-extenal-invitations.action';
import { getExternalInvitations } from '@/domain/external-invitation/actions/get-external-invitations.action';
import { selectVenueScoresByMonth } from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { clearImpactCo2Answers } from '@/domain/impact-co2/actions/clear-impact-co2-answers.action';
import { getImpactCo2Answers } from '@/domain/impact-co2/actions/get-impact-co2-answers.action';
import { getImpactEssQuestions } from '@/domain/impact-ess/actions/get-impact-ess-questions.action';
import { getImpactEssResponses } from '@/domain/impact-ess/actions/get-impact-ess-responses.action';
import { ModelVersionESS } from '@/domain/project/entities/project.entity';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { deleteMultipleVenueDates } from '@/domain/project-venue-dates/actions/delete-multiple-venue-dates.action';
import { downloadVenueImportCsvTemplate } from '@/domain/project-venue-dates/actions/download-venue-import-csv-template.action';
import { VenueDateBasic } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { selectProjectVenuesDates } from '@/domain/project-venue-dates/project-venue-dates.selectors';
import { displaySnackbarWarning } from '@/domain/snackbar/actions/show-snackbar.action';
import { BackButton } from '@/ui/components/button/BackButton';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSToggleButtonGroup } from '@/ui/components/button/FSToggleButtonGroup';
import { FSCard } from '@/ui/components/FSCard';
import { scrollQuizPanelToIdSmooth } from '@/ui/components/quiz-panel/QuizPanel.utils';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { DayQuizPanel } from './components/DayQuizPanel';
import { ModalAddDates } from './components/modal-add-dates/ModalAddDates';
import { ModalImportApiHeeds } from './components/modal-import-api-heeds/ModalImportApiHeeds';
import { ModalImportApiOrfeoVenue } from './components/modal-import-api-orfeo/ModalImportApiOrfeoVenue';
import { ModalImportCsv } from './components/modal-import-csv/ModalImportCsv';
import { MonthQuizPanel } from './components/MonthQuizPanel';
import { VenueDatesContext } from './components/VenueDatesContext';
import { VenueDatesQuizPanel } from './components/VenueDatesQuizPanel';
import { CalendarsView } from './view-calendar/CalendarsView';
import { SelectionToolbar } from './view-list/components/SelectionToolbar';
import { ListMonthesView } from './view-list/ListMonthesView';
import { useVenueDateSelection } from './view-list/useVenueDateSelection';

export function VenueDates() {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectCurrentProjectVenue);
  const venueDates = useAppSelector(selectProjectVenuesDates);
  const navigate = useNavigate();

  const [addDatesModalOpen, setAddDatesModalOpen] = useState(false);
  const [addDatesModalInitialValues, setAddDatesModalInitialValues] = useState<
    VenueDateBasic[]
  >([]);
  const [addDatesModalFocusedMonth, setAddDatesModalFocusedMonth] = useState<
    string | undefined
  >();

  const [importCsvModalOpen, setImportCsvModalOpen] = useState(false);
  const [importApiHeedsModalOpen, setImportApiHeedsModalOpen] = useState(false);
  const [importApiOrfeoModalOpen, setImportApiOrfeoModalOpen] = useState(false);

  const onClickBack = () => {
    if (project) navigate(`/app/projects/venue/${project.id}`);
  };

  const onClickPresets = () => {
    if (project) navigate(`/app/projects/venue/${project.id}/presets`);
  };

  const onClickImportCsv = () => {
    setImportCsvModalOpen(true);
  };

  const onClickDownloadCsvTemplate = () => {
    dispatch(downloadVenueImportCsvTemplate());
  };

  const onClickImportApiHeeds = () => {
    setImportApiHeedsModalOpen(true);
  };

  const onClickImportApiOrfeo = () => {
    setImportApiOrfeoModalOpen(true);
  };

  const onClickAddDate = (month?: string) => {
    setAddDatesModalFocusedMonth(month);
    setAddDatesModalOpen(true);
  };

  const onClickCalendarDayNewDate = (date: Dayjs) => {
    // check if date is in season range
    if (date.isBefore(project?.startDate) || date.isAfter(project?.endDate)) {
      dispatch(
        displaySnackbarWarning({
          message: translate('ProjectsVenue.date-out-of-season-range'),
        }),
      );
      return;
    }
    const dates: VenueDateBasic[] = [{ name: '', date: date.toISOString() }];
    openAddDatesModalWithInitialValues(dates);
  };

  const openAddDatesModalWithInitialValues = (dates: VenueDateBasic[]) => {
    setAddDatesModalInitialValues(dates);
    setAddDatesModalOpen(true);
  };

  if (project === null) {
    return null;
  }

  return (
    <Stack sx={{ gap: 3 }}>
      <PageTitleCustom
        backButtonText={project.name || ''}
        onClickBack={onClickBack}
        onClickAddDate={onClickAddDate}
        onClickImportCsv={onClickImportCsv}
        onClickDownloadCsvTemplate={onClickDownloadCsvTemplate}
        onClickPresets={onClickPresets}
        onClickImportApiHeeds={onClickImportApiHeeds}
        onClickImportApiOrfeo={onClickImportApiOrfeo}
      />
      {venueDates.length === 0 ? (
        <>
          <NoDatesMessageAndCards
            onClickAddDate={onClickAddDate}
            onClickImportCsv={onClickImportCsv}
            onClickDownloadCsvTemplate={onClickDownloadCsvTemplate}
          />
          <CalendarsView
            startDate={project.startDate || ''}
            endDate={project.endDate || ''}
            onClickDate={onClickCalendarDayNewDate}
          />
        </>
      ) : (
        <VenueDateMainContent
          project={project}
          onClickAddDate={onClickAddDate}
          onClickPresets={onClickPresets}
          onClickCalendarDayNewDate={onClickCalendarDayNewDate}
        />
      )}
      <ModalAddDates
        open={addDatesModalOpen}
        onClose={() => {
          setAddDatesModalInitialValues([]);
          setAddDatesModalOpen(false);
          setAddDatesModalFocusedMonth(undefined);
        }}
        project={project}
        initialDates={addDatesModalInitialValues}
        focusedMonth={addDatesModalFocusedMonth}
      />
      <ModalImportCsv
        open={importCsvModalOpen}
        onClose={() => setImportCsvModalOpen(false)}
        onImportDates={openAddDatesModalWithInitialValues}
        project={project}
      />
      <ModalImportApiHeeds
        open={importApiHeedsModalOpen}
        onClose={() => setImportApiHeedsModalOpen(false)}
        onImportDates={openAddDatesModalWithInitialValues}
      />
      <ModalImportApiOrfeoVenue
        open={importApiOrfeoModalOpen}
        onClose={() => setImportApiOrfeoModalOpen(false)}
        onImportDates={openAddDatesModalWithInitialValues}
      />
    </Stack>
  );
}

function VenueDateMainContent({
  project,
  onClickAddDate,
  onClickPresets,
  onClickCalendarDayNewDate,
}: {
  project: ProjectVenue;
  onClickAddDate: (month: string) => void;
  onClickPresets: () => void;
  onClickCalendarDayNewDate: (date: Dayjs) => void;
}) {
  const dispatch = useAppDispatch();

  const [displayType, setDisplayType] = useState<DatesDisplayType>('list');
  const [panelOpen, setPanelOpen] = useState(false);

  const [dayPanelIsVisible, setDayPanelIsVisible] = useState(false);
  const [selectedDayId, setSelectedDayId] = useState<number>(NaN);

  const [monthPanelIsVisible, setMonthPanelIsVisible] = useState(false);
  const [selectedMonthDate, setSelectedMonthDate] = useState<string>('');

  const venueDates = useAppSelector(selectProjectVenuesDates);
  const selectedDates = venueDates.map((date) => date.date);

  const scoresByMonth = useAppSelector(selectVenueScoresByMonth(project.id));

  const onDeleteMultiple = async (ids: number[]) => {
    await dispatch(deleteMultipleVenueDates({ venueDateIds: ids }));
  };

  const {
    isSelecting,
    enterSelecting,
    selectedIds,
    onToggleSelect,
    onToggleSelectMonth,
    toolbarProps,
  } = useVenueDateSelection(venueDates, onDeleteMultiple);

  const onClickEditDay = async (
    id: number,
    modelESS: ModelVersionESS,
    scrollTo?: string,
  ) => {
    dispatch(getImpactCo2Answers({ projectId: id }));
    await dispatch(getImpactEssQuestions({ essModelVersion: modelESS }));
    await dispatch(getImpactEssResponses({ projectId: id }));
    dispatch(getExternalInvitations({ projectId: id }));
    setSelectedDayId(id);
    if (scrollTo) {
      scrollQuizPanelToIdSmooth(scrollTo);
    }
    setDayPanelIsVisible(true);
  };

  const onClickEditMonth = (monthDate: string) => {
    setMonthPanelIsVisible(true);
    setSelectedMonthDate(monthDate);
  };

  const onClickCalendarDay = (date: Dayjs) => {
    // check if this day is already used in venueDates
    const venueDateFound = venueDates.find((x) =>
      dayjs(x.date).isSame(date, 'day'),
    );
    if (venueDateFound) {
      // click on day with date, navigate to list view and focus on day
      navigateToDate(date);
    } else {
      // click on day without date, open modal add-dates and fill first date field
      onClickCalendarDayNewDate(date);
    }
  };

  const navigateToDate = (date: Dayjs) => {
    // show list view
    setDisplayType('list');

    // found the date in the list
    const dateFound = venueDates.find((x) => dayjs(x.date).isSame(date, 'day'));
    if (!dateFound) {
      return;
    }

    // open side panel on date found (first day found is several date on same day)
    onClickEditDay(dateFound.id, dateFound.models.ess as ModelVersionESS);
  };

  const onCloseDayPanel = () => {
    dispatch(clearImpactCo2Answers());
    dispatch(clearExternalInvitations());
    setDayPanelIsVisible(false);
  };

  const onCloseMonthPanel = () => {
    setMonthPanelIsVisible(false);
  };

  const displaySelectDatesButton = displayType === 'list' && !isSelecting;

  return (
    <VenueDatesContext.Provider
      value={{
        panelOpen,
        setPanelOpen,
      }}
    >
      <Stack sx={{ gap: 3 }} width={'calc(100% - 21rem)'}>
        <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }} />
          <DatesDisplayToggle
            displayType={displayType}
            setDisplayType={setDisplayType}
          />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <FSPrimaryButton
              variant="outlined"
              color="info"
              onClick={enterSelecting}
              sx={{
                visibility: displaySelectDatesButton ? 'visible' : 'hidden',
              }}
            >
              {translate('ProjectsVenue.select-dates')}
            </FSPrimaryButton>
          </Box>
        </Stack>
        {displayType === 'list' && isSelecting && (
          <SelectionToolbar {...toolbarProps} />
        )}
        {displayType === 'list' && (
          <ListMonthesView
            onClickAddDate={onClickAddDate}
            onClickEditDay={onClickEditDay}
            onClickEditMonth={onClickEditMonth}
            onClickPresets={onClickPresets}
            isSelecting={isSelecting}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            onToggleSelectMonth={onToggleSelectMonth}
          />
        )}
        {displayType === 'calendars' && (
          <CalendarsView
            startDate={project.startDate || ''}
            endDate={project.endDate || ''}
            selectedDays={selectedDates}
            scoresByMonth={scoresByMonth}
            onClickDate={onClickCalendarDay}
          />
        )}
        <VenueDatesQuizPanel onClickPresets={onClickPresets} />
        {dayPanelIsVisible && (
          <DayQuizPanel
            dateId={selectedDayId}
            onClose={onCloseDayPanel}
            modelESS={project.models.events.ess}
          />
        )}
        {monthPanelIsVisible && (
          <MonthQuizPanel
            monthDate={selectedMonthDate}
            onClose={onCloseMonthPanel}
          />
        )}
      </Stack>
    </VenueDatesContext.Provider>
  );
}

function PageTitleCustom({
  onClickBack,
  onClickAddDate,
  onClickImportCsv,
  onClickDownloadCsvTemplate,
  onClickImportApiHeeds,
  onClickImportApiOrfeo,
  backButtonText,
  onClickPresets,
}: {
  onClickBack: () => void;
  onClickAddDate: () => void;
  onClickImportCsv: () => void;
  onClickDownloadCsvTemplate: () => void;
  onClickImportApiHeeds: () => void;
  onClickImportApiOrfeo: () => void;
  onClickPresets: () => void;
  backButtonText: string;
}) {
  return (
    <Stack>
      <Box mb={2}>
        <BackButton onClick={onClickBack} text={backButtonText} />
      </Box>
      <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
        <Typography variant="h5">{translate('ProjectsVenue.dates')}</Typography>
        <ActionsMenu
          onClickAddDate={onClickAddDate}
          onClickImportCsv={onClickImportCsv}
          onClickDownloadCsvTemplate={onClickDownloadCsvTemplate}
          onClickImportApiHeeds={onClickImportApiHeeds}
          onClickImportApiOrfeo={onClickImportApiOrfeo}
        />
        <FSPrimaryButton onClick={onClickPresets}>
          {translate('ProjectsVenue.presets')}
        </FSPrimaryButton>
        <Box sx={{ width: 100 }} />
      </Stack>
    </Stack>
  );
}

function ActionsMenu({
  onClickAddDate,
  onClickImportCsv,
  onClickDownloadCsvTemplate,
  onClickImportApiHeeds,
  onClickImportApiOrfeo,
}: {
  onClickAddDate: () => void;
  onClickImportCsv: () => void;
  onClickDownloadCsvTemplate: () => void;
  onClickImportApiHeeds: () => void;
  onClickImportApiOrfeo: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>(undefined);

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(undefined);
  };

  const onClickItem = (actionToCall: () => void) => {
    onClose();
    actionToCall();
  };

  return (
    <>
      <FSPrimaryButton startIcon={<MoreVertOutlined />} onClick={onClick}>
        {translate('ProjectsVenue.add')}
      </FSPrimaryButton>
      <Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl}>
        <MenuList>
          <MenuItem onClick={() => onClickItem(onClickAddDate)}>
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ProjectsVenue.add-dates')}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => onClickItem(onClickImportCsv)}>
            <ListItemIcon>
              <PostAddOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('Projects.import-csv')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => onClickItem(onClickDownloadCsvTemplate)}>
            <ListItemIcon>
              <Download fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('ProjectsVenue.download-csv-template')}
            </ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => onClickItem(onClickImportApiHeeds)}>
            <ListItemIcon>
              <CloudSyncOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('Projects.import-api-heeds')}
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={() => onClickItem(onClickImportApiOrfeo)}>
            <ListItemIcon>
              <CloudSyncOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('Projects.import-api-orfeo')}
            </ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
}

function NoDatesMessageAndCards({
  onClickAddDate,
  onClickImportCsv,
  onClickDownloadCsvTemplate,
}: {
  onClickAddDate: () => void;
  onClickImportCsv: () => void;
  onClickDownloadCsvTemplate: () => void;
}) {
  return (
    <>
      <Box display={'flex'} justifyContent={'center'}>
        <Typography
          fontSize={40}
          maxWidth={500}
          textAlign={'center'}
          fontWeight={800}
        >
          {translate('ProjectsVenue.no-date-for-now')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item md={4} xs={12}>
          <FSCard variant="elevation" sx={addDateCardStyle}>
            <AddOutlined sx={{ fontSize: 36 }} />
            <Typography
              mt={3}
              fontSize={30}
              fontWeight={800}
              textAlign={'center'}
            >
              {translate('ProjectsVenue.manually-add-dates')}
            </Typography>
            <FSPrimaryButton sx={{ mt: 3 }} onClick={() => onClickAddDate()}>
              {translate('ProjectsVenue.add-dates')}
            </FSPrimaryButton>
          </FSCard>
        </Grid>
        <Grid item md={4} xs={12}>
          <FSCard variant="elevation" sx={addDateCardStyle}>
            <PostAddOutlined sx={{ fontSize: 36 }} />
            <Typography
              mt={3}
              fontSize={30}
              fontWeight={800}
              textAlign={'center'}
            >
              {translate('ProjectsVenue.import-csv-table')}
            </Typography>
            <FSPrimaryButton sx={{ mt: 3 }} onClick={onClickImportCsv}>
              {translate('Projects.import-csv')}
            </FSPrimaryButton>
            <FSPrimaryButton
              color="info"
              sx={{ mt: 2 }}
              onClick={onClickDownloadCsvTemplate}
            >
              {translate('ProjectsVenue.download-template')}
            </FSPrimaryButton>
          </FSCard>
        </Grid>
        <Grid item md={4} xs={12}>
          <FSCard variant="elevation" sx={addDateCardStyle}>
            <CloudSyncOutlined sx={{ fontSize: 36 }} />
            <Typography
              mt={3}
              fontSize={30}
              fontWeight={800}
              textAlign={'center'}
            >
              {translate('ProjectsVenue.link-by-api')}
            </Typography>
            <FSPrimaryButton sx={{ mt: 3 }} disabled>
              {translate('ProjectsVenue.link-ticketing')}
            </FSPrimaryButton>
          </FSCard>
        </Grid>
      </Grid>
    </>
  );
}

type DatesDisplayType = 'list' | 'calendars';

function DatesDisplayToggle({
  displayType,
  setDisplayType,
}: {
  displayType: DatesDisplayType;
  setDisplayType: React.Dispatch<React.SetStateAction<DatesDisplayType>>;
}) {
  const displayValues: { id: DatesDisplayType; value: React.ReactNode }[] = [
    {
      id: 'list',
      value: (
        <Typography>
          {translate('ProjectsVenue.DatesDisplayType.list')}
        </Typography>
      ),
    },
    {
      id: 'calendars',
      value: (
        <Typography>
          {translate('ProjectsVenue.DatesDisplayType.calendars')}
        </Typography>
      ),
    },
  ];

  return (
    <FSToggleButtonGroup
      selectedValueId={displayType}
      values={displayValues}
      onChange={(value) => setDisplayType(value as DatesDisplayType)}
    />
  );
}

const addDateCardStyle = {
  px: 3,
  py: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
};
